// ─────────────────────────────────────────────────────────────────────────────
// Markdown → React, sin HTML crudo.
//
// Los documentos de la Biblioteca los escriben agentes de IA sobre archivos del servidor.
// Renderizarlos con `dangerouslySetInnerHTML` sería abrir una puerta: bastaría que un
// informe trajera una etiqueta <script> (por error o por un ataque que llegara a escribir
// en esa carpeta) para ejecutar código en el navegador del administrador. Por eso acá el
// markdown se convierte en ELEMENTOS React: el texto siempre es texto, y el HTML embebido
// se muestra literal, nunca se interpreta.
//
// Cubre lo que usan los informes de la cuadrilla: encabezados, párrafos, listas (con
// casillas [ ]/[x]), tablas, bloques de código, citas, reglas, negrita, cursiva, código en
// línea y enlaces (solo http/https/mailto). Lo que no reconoce lo deja como texto.
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";

const ENLACE_SEGURO = /^(https?:\/\/|mailto:)/i;

/** Convierte una línea con *negrita*, `código`, enlaces… en un arreglo de nodos. */
export const renderInline = (texto, key = "i") => {
  const partes = [];
  // Orden: código en línea primero (para que dentro no se interpreten otros símbolos),
  // después enlaces, negrita, cursiva y tachado.
  const re = /(`[^`]+`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*([^*]+)\*\*)|(__([^_]+)__)|(~~([^~]+)~~)|(\*([^*\s][^*]*)\*)|(_([^_\s][^_]*)_)/g;
  let ultimo = 0;
  let m;
  let n = 0;
  while ((m = re.exec(texto)) !== null) {
    if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
    const k = `${key}-${n += 1}`;
    if (m[1]) partes.push(<code key={k} className="bib-code">{m[1].slice(1, -1)}</code>);
    else if (m[2]) {
      const href = m[4];
      if (ENLACE_SEGURO.test(href)) partes.push(<a key={k} href={href} target="_blank" rel="noopener noreferrer">{m[3]}</a>);
      else partes.push(<span key={k}>{m[3]} <span className="bib-muted">({href})</span></span>);
    } else if (m[5]) partes.push(<strong key={k}>{m[6]}</strong>);
    else if (m[7]) partes.push(<strong key={k}>{m[8]}</strong>);
    else if (m[9]) partes.push(<del key={k}>{m[10]}</del>);
    else if (m[11]) partes.push(<em key={k}>{m[12]}</em>);
    else if (m[13]) partes.push(<em key={k}>{m[14]}</em>);
    ultimo = m.index + m[0].length;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes;
};

const esSeparadorTabla = (l) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(l);
const celdas = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

/**
 * Parser por bloques: recorre las líneas y arma párrafos, listas, tablas, código…
 * Devuelve elementos React listos para pintar.
 */
export const renderMarkdown = (src) => {
  const lineas = (src || "").replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  let i = 0;
  let k = 0;
  const key = () => `b${k += 1}`;

  while (i < lineas.length) {
    const l = lineas[i];

    if (!l.trim()) { i += 1; continue; }

    // Bloque de código ```
    if (/^\s*```/.test(l)) {
      const lang = l.trim().slice(3).trim();
      const buf = [];
      i += 1;
      while (i < lineas.length && !/^\s*```/.test(lineas[i])) { buf.push(lineas[i]); i += 1; }
      i += 1;
      out.push(<pre key={key()} className="bib-pre" data-lang={lang}><code>{buf.join("\n")}</code></pre>);
      continue;
    }

    // Encabezados
    const h = l.match(/^(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (h) {
      const nivel = h[1].length;
      const Tag = `h${Math.min(nivel + 1, 6)}`; // el h1 del documento ya lo pone la pantalla
      out.push(<Tag key={key()} className={`bib-h bib-h${nivel}`}>{renderInline(h[2], key())}</Tag>);
      i += 1;
      continue;
    }

    // Regla horizontal
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(l)) { out.push(<hr key={key()} className="bib-hr" />); i += 1; continue; }

    // Tabla: encabezado + línea separadora
    if (l.includes("|") && i + 1 < lineas.length && esSeparadorTabla(lineas[i + 1])) {
      const cab = celdas(l);
      i += 2;
      const filas = [];
      while (i < lineas.length && lineas[i].includes("|") && lineas[i].trim()) { filas.push(celdas(lineas[i])); i += 1; }
      const kt = key();
      out.push(
        <div key={kt} className="bib-tabla-wrap">
          <table className="bib-tabla">
            <thead><tr>{cab.map((c, j) => <th key={j}>{renderInline(c, `${kt}h${j}`)}</th>)}</tr></thead>
            <tbody>{filas.map((f, r) => <tr key={r}>{cab.map((_, j) => <td key={j}>{renderInline(f[j] || "", `${kt}r${r}c${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }

    // Cita
    if (/^\s*>/.test(l)) {
      const buf = [];
      while (i < lineas.length && /^\s*>/.test(lineas[i])) { buf.push(lineas[i].replace(/^\s*>\s?/, "")); i += 1; }
      out.push(<blockquote key={key()} className="bib-cita">{renderMarkdown(buf.join("\n"))}</blockquote>);
      continue;
    }

    // Listas (con anidación por sangría y casillas [ ] / [x])
    if (/^\s*([-*+]|\d+[.)])\s+/.test(l)) {
      const items = [];
      const ordenada = /^\s*\d+[.)]\s+/.test(l);
      while (i < lineas.length && /^\s*([-*+]|\d+[.)])\s+/.test(lineas[i])) {
        const m = lineas[i].match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/);
        const sangria = m[1].length;
        let texto = m[3];
        i += 1;
        // Continuaciones: líneas más sangradas que no empiezan otro ítem
        while (i < lineas.length && lineas[i].trim() && !/^\s*([-*+]|\d+[.)])\s+/.test(lineas[i]) && /^\s{2,}/.test(lineas[i])) {
          texto += " " + lineas[i].trim();
          i += 1;
        }
        items.push({ sangria, texto });
      }
      const kl = key();
      const pintar = (arr, nivel) => {
        const nodos = [];
        let j = 0;
        while (j < arr.length) {
          const it = arr[j];
          const hijos = [];
          j += 1;
          while (j < arr.length && arr[j].sangria > it.sangria) { hijos.push(arr[j]); j += 1; }
          const casilla = it.texto.match(/^\[( |x|X)\]\s+(.*)$/);
          const contenido = casilla ? casilla[2] : it.texto;
          nodos.push(
            <li key={`${nivel}-${j}`} className={casilla ? (casilla[1] === " " ? "bib-todo" : "bib-done") : undefined}>
              {casilla && <span className="bib-check" aria-hidden="true">{casilla[1] === " " ? "☐" : "☑"}</span>}
              {renderInline(contenido, `${kl}l${nivel}${j}`)}
              {hijos.length > 0 && pintar(hijos, nivel + 1)}
            </li>
          );
        }
        return ordenada && nivel === 0 ? <ol className="bib-lista">{nodos}</ol> : <ul className="bib-lista">{nodos}</ul>;
      };
      out.push(<div key={kl}>{pintar(items, 0)}</div>);
      continue;
    }

    // Párrafo: líneas seguidas hasta una en blanco o el inicio de otro bloque
    const buf = [l];
    i += 1;
    while (i < lineas.length && lineas[i].trim() && !/^(\s*```|#{1,6}\s|\s*>|\s*([-*+]|\d+[.)])\s+|\s*(-{3,}|\*{3,})\s*$)/.test(lineas[i]) && !(lineas[i].includes("|") && i + 1 < lineas.length && esSeparadorTabla(lineas[i + 1]))) {
      buf.push(lineas[i]);
      i += 1;
    }
    out.push(<p key={key()} className="bib-p">{renderInline(buf.join(" "), key())}</p>);
  }
  return out;
};

const Markdown = ({ texto }) => <div className="bib-md">{renderMarkdown(texto)}</div>;

export default Markdown;
